import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Image from '../../../../shared/components/Image';
import { testIds } from '../../../../core/testIds';
import {FavoritesIconOff, FavoritesIconOn} from './icons/';

import styles from './BackToGameToast.module.css';
import { useHideNavFooterOnScroollMobile } from 'shared/hooks/useHideNavFooterOnScroollMobile';

interface BackToGameToastProps {
    gameId: string;
    gameName: string;
    gameThumbnail: string;
    onPlay: (gameId: string) => void;
    onFavorite: (gameId: string) => void;
    isFavorite?: boolean;
    isSearchActive?: boolean;
}

const BackToGameToast: React.FC<BackToGameToastProps> = ({
    gameId,
    gameName,
    gameThumbnail,
    onPlay,
    onFavorite,
    isFavorite = false,
    isSearchActive = false
}) => {
    const { t } = useTranslation();
    const [showToast, setShowToast] = useState<boolean>(false);
    const shouldHide = useHideNavFooterOnScroollMobile();

    useEffect(() => {
        // Initial check
        const isContinuePlayingEnabled = localStorage.getItem('lobby_continuePlaying') !== 'false';
        setShowToast(isContinuePlayingEnabled);

        // For changes in other tabs
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'lobby_continuePlaying') {
                const isEnabled = e.newValue !== 'false';
                setShowToast(isEnabled);
            }
        };

        // For changes in the same tab
        const handleCustomEvent = (e: Event) => {
            const customEvent = e as CustomEvent<{ enabled: boolean }>;
            setShowToast(customEvent.detail.enabled);
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('continuePlayingChanged', handleCustomEvent as EventListener);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('continuePlayingChanged', handleCustomEvent as EventListener);
        };
    }, []);

    const handleClose = () => {
        toast.dismiss('back-to-game');
    };

    const handlePlay = (e: React.MouseEvent) => {
        e.stopPropagation();
        onPlay(gameId);
        handleClose();
    };

    const handleFavoriteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onFavorite(gameId);
    };

    const handleCloseClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        handleClose();
    };

    return (
        <div className={`${styles.lobbyBackToGameToast} ${isSearchActive ? 'isSearchActive' : ''}`} onClick={handlePlay} data-continue-playing={showToast ? 'on' : 'off'} data-testid={testIds.games.backToGameToast} data-hide={shouldHide ? 'true' : 'false'}>
            <div className={styles.lobbyBackToGameContent}>
                <div className={styles.lobbyGameThumbnail}>
                    <Image
                        src={gameThumbnail}
                        alt={gameName}
                        width={60}
                        height={60}
                    />
                </div>
                <div className={styles.lobbyGameInfo}>
                    <span className={styles.lobbyContinueLabel} data-testid={testIds.games.backToGameContinueLabel}>{t('generic_continue_playing', 'Continue Playing')}</span>
                    <span className={styles.lobbyGameName} data-testid={testIds.games.backToGameName}>{gameName}</span>
                </div>
            </div>
            <div className={styles.lobbyActionButtons}>
                <button
                    className={styles.lobbyFavoriteBtn}
                    onClick={handleFavoriteClick}
                    aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                >
                    {isFavorite ? <FavoritesIconOn className={styles.icon} /> : <FavoritesIconOff className={styles.icon} />}
                </button>
                <Image
                    src="/ui/popupCloseRed"
                    manualAffix=".svg"
                    alt="Close"
                    onClick={handleCloseClick}
                    className={styles.lobbyCloseBtn}
                />
            </div>
        </div>
    );
};

export default BackToGameToast;
